

import { Visual }                from '../visuals/visual';
import { isRectangle }           from '../visuals/rectangle';
import { isText }                from '../visuals/text';
import { isCircle }              from '../visuals/circle';
import { isLine }                from '../visuals/line';
import { EventHandlerInterface } from '../events/event.handler.interface';

import { KonvaEventObject }      from 'konva/lib/Node';
import Konva                     from 'konva';
import { LineCap }               from 'konva/lib/Shape';

/**
 *
 * @class KonvaObjectFactory
 *
 * produce Konva objects (rendered on the canvas) from requested visuals.
 * This is where we turn the specification of objects to draw into the actual
 * drawing.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class KonvaObjectFactory {

  private static bind(shape: Konva.Shape, handler: EventHandlerInterface|null|undefined): Konva.Shape {

    if (!handler) {

      /* behaviors being bound */

      return shape;
    }

    shape.on('mouseover', (e:KonvaEventObject<MouseEvent>): void => {

      handler.onMouseOver();
    });

    shape.on('mouseout', (e:KonvaEventObject<MouseEvent>): void => {

      handler.onMouseOut();
    });

    shape.on('click', (e:KonvaEventObject<MouseEvent>): void => {

      handler.onClick();
    });

    shape.on('dblclick', (e:KonvaEventObject<MouseEvent>): void => {

      handler.onDoubleClick();
    });

    return shape;
  }

  /** convenience to build a Konva canvas object that actually renders our visual */
  public static build(item: Visual, eventHandler: EventHandlerInterface|null|undefined = null): Konva.Shape|null {

    if (isRectangle(item)) {

      return KonvaObjectFactory.bind(new Konva.Rect({
        x:            item.x               ? item.x               : 0,
        y:            item.y               ? item.y               : 0,
        width:        item.width           ? item.width           : 10,
        height:       item.height          ? item.height          : 10,
        cornerRadius: item.cornerRadius    ? item.cornerRadius    : 0,
        fill:         item.backgroundColor ? item.backgroundColor : 'green',
        stroke:       item.borderColor     ? item.borderColor     : 'red',
        strokeWidth:  item.borderWidth     ? item.borderWidth     : 2,
        visible:      item.visible         ? item.visible         : true,
        opacity:      item.opacity         ? item.opacity         : 1.0,
      }), eventHandler);

    } else if (isText(item)) {

      return KonvaObjectFactory.bind(new Konva.Text({
        x:            item.x         ? item.x         : 0,
        y:            item.y         ? item.y         : 0,
        text:         item.text      ? item.text      : 'XO',
        fill:         item.color     ? item.color     : 'white',
        family:       item.family    ? item.family    : 'Arial',
        fontSize:     item.size      ? item.size      : 10,
        fontWeight:   item.weight    ? item.weight    : 'normal',
        fontVariant:  item.variation ? item.variation : '',
        fontStyle:    item.style     ? item.style     : '',
        visible:      item.visible   ? item.visible   : true,
        opacity:      item.opacity   ? item.opacity   : 1.0,
        rotation:     item.rotation  ? item.rotation  : 0.0,
      }), eventHandler);

    } else if (isCircle(item)) {

      return KonvaObjectFactory.bind(new Konva.Circle({
        x:            item.x               ? item.x               : 0,
        y:            item.y               ? item.y               : 0,
        radius:       item.radius          ? item.radius          : 10,
        fill:         item.backgroundColor ? item.backgroundColor : 'white',
        stroke:       item.borderColor     ? item.borderColor     : 'black',
        strokeWidth:  item.borderWidth     ? item.borderWidth     : 2,
        visible:      item.visible         ? item.visible         : true,
        opacity:      item.opacity         ? item.opacity         : 1.0
      }), eventHandler);

    } else if (isLine(item)) {

      let points: number[] = [];

      if (item.points) {

        item.points.forEach((point: {x: number, y: number}): void => {

          points.push(point.x, point.y);

        });

      } else {

        if (item.x && item.y) {

          points.push(item.x, item.y, item.x, item.y)
        }

      }

      let lineCap: LineCap = 'square';

      if (item.lineCap && item.lineCap == 'round') {

        lineCap = 'round';
      }

      if (item.lineCap && item.lineCap == 'butt') {

        lineCap = 'butt';
      }

      return KonvaObjectFactory.bind(new Konva.Line({
        points,
        fill:         item.color     ? item.color     : 'white',
        stroke:       item.color     ? item.color     : 'black',
        strokeWidth:  item.lineWidth ? item.lineWidth : 2,
        visible:      item.visible   ? item.visible   : true,
        opacity:      item.opacity   ? item.opacity   : 1.0,
        lineCap
      }), eventHandler);

    }

    /* not match for visual */

    return null;
  }
}
